import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowUpRight, ArrowDownRight, Wallet, TrendingUp, TrendingDown } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line } from 'recharts';
import { toast } from 'sonner';
import HealthOverview from '@/features/financial-health/components/HealthOverview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [categoryExpenses, setCategoryExpenses] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      // Fetch core dashboard stats first
      const [statsRes, categoryRes, trendRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, config),
        axios.get(`${API}/dashboard/category-expenses`, config),
        axios.get(`${API}/dashboard/monthly-trend`, config),
      ]);

      setStats(statsRes.data);
      setCategoryExpenses(categoryRes.data);
      setMonthlyTrend(trendRes.data);
    } catch (error) {
      console.error('Core dashboard data failed:', error);
      toast.error('Failed to load dashboard stats');
    }

    // Fetch health data separately so it doesn't block the main dashboard
    try {
      const healthRes = await axios.get(`${API}/financial-health/dashboard`, config);
      if (healthRes.data.success) {
        setHealthData(healthRes.data.data);
      }
    } catch (error) {
      console.error('Health data failed:', error);
      // We don't toast error here to avoid annoying the user if they just haven't set it up yet
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const COLORS = ['#0F172A', '#10B981', '#EF4444', '#F59E0B', '#6366F1', '#8B5CF6', '#EC4899', '#06B6D4'];

  const summaryCards = [
    {
      title: 'Total Balance',
      value: stats?.totalBalance || 0,
      icon: Wallet,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      testId: 'total-balance-card'
    },
    {
      title: 'Total Income',
      value: stats?.totalIncome || 0,
      icon: ArrowUpRight,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      testId: 'total-income-card'
    },
    {
      title: 'Total Expense',
      value: stats?.totalExpense || 0,
      icon: ArrowDownRight,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      testId: 'total-expense-card'
    },
    {
      title: 'Current Month Income',
      value: stats?.currentMonthIncome || 0,
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
      testId: 'current-month-income-card'
    },
    {
      title: 'Current Month Expense',
      value: stats?.currentMonthExpense || 0,
      icon: TrendingDown,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      testId: 'current-month-expense-card'
    },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight" data-testid="dashboard-title">Dashboard</h1>
        <p className="text-muted-foreground mt-2">Overview of your financial health & wealth</p>
      </div>

      {/* Financial Health Overview - Newly Added */}
      {healthData && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
          <HealthOverview data={healthData} />
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {summaryCards.map((card, index) => {
          const Icon = card.icon;
          return (
            <Card key={index} className="hover:shadow-md transition-shadow" data-testid={card.testId}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">{card.title}</CardTitle>
                <div className={`p-2 rounded-lg ${card.bgColor}`}>
                  <Icon className={`h-4 w-4 ${card.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold currency">{formatCurrency(card.value)}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pie Chart - Expense by Category */}
        <Card data-testid="expense-pie-chart-card">
          <CardHeader>
            <CardTitle>Expense by Category</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryExpenses.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryExpenses}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="amount"
                    nameKey="category"
                  >
                    {categoryExpenses.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No expense data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bar Chart - Monthly Income vs Expense */}
        <Card data-testid="monthly-bar-chart-card">
          <CardHeader>
            <CardTitle>Monthly Income vs Expense</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyTrend.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyTrend}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="income" fill="#10B981" name="Income" />
                  <Bar dataKey="expense" fill="#EF4444" name="Expense" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                No monthly data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line Chart - Last 6 Months Spending Trend */}
      <Card data-testid="spending-trend-chart-card">
        <CardHeader>
          <CardTitle>Last 6 Months Spending Trend</CardTitle>
        </CardHeader>
        <CardContent>
          {monthlyTrend.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border/50" />
                <XAxis dataKey="month" className="text-xs" />
                <YAxis className="text-xs" />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} name="Expense" />
                <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} name="Income" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[300px] text-muted-foreground">
              No trend data available
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;